import { TrashIcon, ExclamationTriangleIcon, NoSymbolIcon } from '@heroicons/react/24/solid';
import type { ChangeEvent } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import React from 'react';
import { apiUrl, authFetch, authService } from '../utils/api';

interface Teacher {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  biography: string;
  qualifications: string;
  department: string;
  yearsOfExperience: number;
  imagePath: string | null;
  active: boolean;
  hireDate: string;
  createdDate: string;
  modificationDate: string;
  subjectCount: number;
  courseCount: number;
  teacherCode: number;
  whatsApp: string | null;
  facebook: string | null;
  tikTok: string | null;
  youTube: string | null;
}

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  password: string;
  biography: string;
  yearsOfExperience: number;
  imagePath: string;
  active: boolean;
  subjectIds: string[];
  teacherCode: number;
  whatsApp: string;
  facebook: string;
  tikTok: string;
  youTube: string;
  imageFile: File | null;
}

interface Filters {
  name: string;
  active: string;
}

const Teachers = () => {
  const navigate = useNavigate();
  const [showAddForm, setShowAddForm] = useState(false);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [filters, setFilters] = useState<Filters>({
    name: '',
    active: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [teacherToDelete, setTeacherToDelete] = useState<Teacher | null>(null);
  const [teacherToBlock, setTeacherToBlock] = useState<Teacher | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    biography: '',
    yearsOfExperience: 0,
    imagePath: '',
    active: true,
    subjectIds: [],
    teacherCode: 0,
    whatsApp: '',
    facebook: '',
    tikTok: '',
    youTube: '',
    imageFile: null,
  });
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  
  // Maximum file size (5MB)
  const MAX_FILE_SIZE = 5 * 1024 * 1024;
  // Allowed file types
  const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/gif'];

  // Fetch teachers from API
  const fetchTeachers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = new URLSearchParams();
      if (filters.active) queryParams.append('active', filters.active);
      if (filters.name) queryParams.append('name', filters.name);

      const response = await authFetch(
        apiUrl(`Teacher?${queryParams.toString()}`),
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authService.getToken()}`
          }
        }
      );
      if (!response.ok) throw new Error('Failed to fetch teachers');
      const data = await response.json();
      
      if (data.success) {
        setTeachers(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch teachers');
      }
    } catch (error) {
      setTeachers([]);
      setError('Failed to fetch teachers. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, [filters.active, filters.name]);

  useEffect(() => {
    fetchTeachers();
  }, [fetchTeachers]);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'teacherCode' || name === 'yearsOfExperience' ? parseInt(value) || 0 : value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImageError(null);
    const file = e.target.files?.[0];
    
    if (!file) {
      setFormData((prev) => ({ ...prev, imageFile: null }));
      setPreviewUrl(null);
      return;
    }

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setImageError('Please select a valid image file (JPEG, PNG, or GIF)');
      e.target.value = '';
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setImageError('Image size should be less than 5MB');
      e.target.value = '';
      return;
    }

    // Create preview URL
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    setFormData((prev) => ({ ...prev, imageFile: file }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      // Create FormData for multipart/form-data submission
      const formDataToSend = new FormData();
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phoneNumber', formData.phoneNumber);
      formDataToSend.append('password', formData.password);
      formDataToSend.append('biography', formData.biography || '');
      formDataToSend.append('yearsOfExperience', formData.yearsOfExperience.toString());
      formDataToSend.append('teacherCode', formData.teacherCode.toString());
      formDataToSend.append('whatsApp', formData.whatsApp || '');
      formDataToSend.append('facebook', formData.facebook || '');
      formDataToSend.append('tikTok', formData.tikTok || '');
      formDataToSend.append('youTube', formData.youTube || '');
      formDataToSend.append('active', formData.active.toString());
      
      if (formData.imageFile) {
        formDataToSend.append('imagePath', formData.imageFile);
      }

      const response = await authFetch(apiUrl('Teacher'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: formDataToSend
      });
      
      const data = await response.json();
      
      if (data.success) {
        setShowAddForm(false);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phoneNumber: '',
          password: '',
          biography: '',
          yearsOfExperience: 0,
          imagePath: '',
          active: true,
          subjectIds: [],
          teacherCode: 0,
          whatsApp: '',
          facebook: '',
          tikTok: '',
          youTube: '',
          imageFile: null,
        });
        setPreviewUrl(null);
        setImageError(null);
        fetchTeachers();
      } else {
        alert('Failed to add teacher: ' + data.message);
      }
    } catch (error) {
      console.error('Error adding teacher:', error);
      alert('Failed to add teacher. Please try again later.');
    }
  };

  const handleDelete = async (id: string) => {
    setIsDeleting(true);
    
    try {
      const response = await authFetch(apiUrl(`Teacher/${id}`), {
        method: 'DELETE',
        headers: {
          'accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        }
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTeachers(teachers.filter(teacher => teacher.id !== id));
        setTeacherToDelete(null);
      } else {
        alert('Failed to delete teacher: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting teacher:', error);
      alert('Failed to delete teacher. Please try again later.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleStatus = async (teacher: Teacher) => {
    try {
      const newActive = !teacher.active;
      const response = await authFetch(apiUrl(`Teacher/${teacher.id}/status`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Accept': '*/*',
          'Authorization': `Bearer ${authService.getToken()}`
        },
        body: JSON.stringify({ active: newActive })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setTeachers(
          teachers.map(t =>
            t.id === teacher.id
              ? { ...t, active: data.data?.active ?? newActive }
              : t
          )
        );
        setTeacherToBlock(null);
      } else {
        alert('Failed to update teacher status: ' + data.message);
      }
    } catch (error) {
      console.error('Error updating teacher status:', error);
      alert('Failed to update teacher status. Please try again later.');
    }
  };

  const handleSearch = () => {
    fetchTeachers();
  };

  const handleResetFilters = () => {
    setFilters({
      name: '',
      active: '',
    });
  };

  // Handle teacher click to navigate to details
  const handleTeacherClick = (teacherId: string, e: { target: EventTarget }) => {
    // Check if the click was on an action button
    const target = e.target as HTMLElement;
    if (target.closest('button')) {
      return; // Don't navigate if clicking on action buttons
    }
    navigate(`/teachers/${teacherId}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" dir="rtl">
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 md:py-8">
        {/* Header */}
        <div className="mb-4 sm:mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                المعلمون
              </h1>
              <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">إدارة المعلمين</p>
            </div>
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg 
                       shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2 
                       focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span className="hidden sm:inline">إضافة مدرس جديد</span>
              <span className="sm:hidden">إضافة</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6 md:mb-8 transition-all duration-200 hover:shadow-xl">
          <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2">
            <div className="space-y-1">
              <label htmlFor="name-filter" className="block text-sm font-medium text-gray-700">
                اسم المعلم
              </label>
              <input
                type="text"
                id="name-filter"
                value={filters.name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilters({ ...filters, name: e.target.value })}
                className="block w-full px-3 sm:px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200 text-sm sm:text-base"
                placeholder="البحث في المعلمين..."
              />
            </div>
            <div className="space-y-1">
              <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700">
                الحالة
              </label>
              <select
                id="status-filter"
                value={filters.active}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, active: e.target.value })}
                className="block w-full px-3 sm:px-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent
                         transition-colors duration-200 text-sm sm:text-base"
              >
                <option value="">جميع الحالات</option>
                <option value="true">نشط</option>
                <option value="false">محظور</option>
              </select>
            </div>
          </div>
          <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-end gap-3 sm:gap-4">
            <button
              onClick={handleSearch}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white text-sm rounded-lg
                       shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 focus:outline-none focus:ring-2
                       focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              تطبيق التصفية
            </button>
            <button
              onClick={handleResetFilters}
              className="w-full sm:w-auto inline-flex items-center justify-center px-4 sm:px-6 py-2 sm:py-2.5 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 text-sm transition-colors duration-200"
            >
              إعادة تعيين
            </button>
          </div>
        </div>

        {/* Teachers Table - Desktop View */}
        <div className="hidden md:block bg-white rounded-xl sm:rounded-2xl shadow-lg overflow-hidden transition-all duration-200 hover:shadow-xl">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <th scope="col" className="px-4 lg:px-6 py-3 lg:py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    اسم المعلم
                  </th>
                  <th scope="col" className="px-4 lg:px-6 py-3 lg:py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    رقم الهاتف
                  </th>
                  <th scope="col" className="px-4 lg:px-6 py-3 lg:py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    تاريخ التسجيل
                  </th>
                  <th scope="col" className="px-4 lg:px-6 py-3 lg:py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    عدد المواد
                  </th>
                  <th scope="col" className="px-4 lg:px-6 py-3 lg:py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    عدد الكورسات
                  </th>
                  <th scope="col" className="px-4 lg:px-6 py-3 lg:py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الحالة
                  </th>
                  <th scope="col" className="px-4 lg:px-6 py-3 lg:py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    الإجراءات
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-4">
                      <div className="flex items-center justify-center text-red-500">
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {error}
                      </div>
                    </td>
                  </tr>
                ) : teachers.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-500">
                        <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <p className="text-lg font-medium">لم يتم العثور على معلمين</p>
                        <p className="text-sm">حاول تعديل التصفية للعثور على ما تبحث عنه</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  teachers.map((teacher) => (
                    <tr 
                      key={teacher.id} 
                      className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                      onClick={(e: { target: EventTarget }) => handleTeacherClick(teacher.id, e)}
                    >
                      <td className="px-4 lg:px-6 py-3 lg:py-4">
                        <div className="text-sm font-medium text-gray-900">{teacher.fullName}</div>
                      </td>
                      <td className="px-4 lg:px-6 py-3 lg:py-4">
                        <div className="text-sm text-gray-600">{teacher.phoneNumber}</div>
                      </td>
                      <td className="px-4 lg:px-6 py-3 lg:py-4">
                        <div className="text-sm text-gray-600">
                          {new Date(teacher.createdDate).toLocaleDateString('ar-EG')}
                        </div>
                      </td>
                      <td className="px-4 lg:px-6 py-3 lg:py-4">
                        <div className="text-sm text-gray-900 font-medium">{teacher.subjectCount}</div>
                      </td>
                      <td className="px-4 lg:px-6 py-3 lg:py-4">
                        <div className="text-sm text-gray-900 font-medium">{teacher.courseCount}</div>
                      </td>
                      <td className="px-4 lg:px-6 py-3 lg:py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                          ${teacher.active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'}`}
                        >
                          {teacher.active ? 'نشط' : 'محظور'}
                        </span>
                      </td>
                      <td className="px-4 lg:px-6 py-3 lg:py-4 text-right">
                        <div className="flex items-center justify-end space-x-reverse space-x-2">
                          <button
                            onClick={() => setTeacherToBlock(teacher)}
                            className={`inline-flex items-center p-2 rounded-lg transition-colors duration-200 ${
                              teacher.active
                                ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50'
                                : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                            }`}
                            title={teacher.active ? 'حظر المعلم' : 'إلغاء حظر المعلم'}
                          >
                            <NoSymbolIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => setTeacherToDelete(teacher)}
                            className="inline-flex items-center p-2 text-red-600 hover:text-red-900 rounded-lg hover:bg-red-50
                                     transition-colors duration-200"
                            title="حذف المعلم"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Teachers Cards - Mobile View */}
        <div className="md:hidden space-y-3">
          {loading ? (
            <div className="bg-white rounded-xl shadow-lg p-8">
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            </div>
          ) : error ? (
            <div className="bg-white rounded-xl shadow-lg p-6">
              <div className="flex items-center justify-center text-red-500">
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {error}
              </div>
            </div>
          ) : teachers.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
              <div className="flex flex-col items-center justify-center text-gray-500">
                <svg className="w-12 h-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-base font-medium">لم يتم العثور على معلمين</p>
                <p className="text-sm mt-1">حاول تعديل التصفية للعثور على ما تبحث عنه</p>
              </div>
            </div>
          ) : (
            teachers.map((teacher) => (
              <div
                key={teacher.id}
                className="bg-white rounded-xl shadow-lg p-4 transition-all duration-200 hover:shadow-xl"
                onClick={(e: React.MouseEvent) => {
                  const target = e.target as HTMLElement;
                  if (!target.closest('button')) {
                    handleTeacherClick(teacher.id, e);
                  }
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">{teacher.fullName}</h3>
                    <p className="text-sm text-gray-600">{teacher.phoneNumber}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                    ${teacher.active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'}`}
                  >
                    {teacher.active ? 'نشط' : 'محظور'}
                  </span>
                </div>
                
                <div className="grid grid-cols-2 gap-3 mb-3 pt-3 border-t border-gray-100">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">تاريخ التسجيل</p>
                    <p className="text-sm text-gray-900">
                      {new Date(teacher.createdDate).toLocaleDateString('ar-EG')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">عدد المواد</p>
                    <p className="text-sm font-medium text-gray-900">{teacher.subjectCount}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">عدد الكورسات</p>
                    <p className="text-sm font-medium text-gray-900">{teacher.courseCount}</p>
                  </div>
                </div>

                <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-100">
                  <button
                    onClick={() => setTeacherToBlock(teacher)}
                    className={`inline-flex items-center p-2 rounded-lg transition-colors duration-200 ${
                      teacher.active
                        ? 'text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50'
                        : 'text-green-600 hover:text-green-900 hover:bg-green-50'
                    }`}
                    title={teacher.active ? 'حظر المعلم' : 'إلغاء حظر المعلم'}
                  >
                    <NoSymbolIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setTeacherToDelete(teacher)}
                    className="inline-flex items-center p-2 text-red-600 hover:text-red-900 rounded-lg hover:bg-red-50
                             transition-colors duration-200"
                    title="حذف المعلم"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Add Teacher Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-3 sm:p-4 z-50">
            <div className="bg-white rounded-lg p-4 sm:p-6 max-w-md w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold mb-4 sm:mb-6">إضافة مدرس جديد</h2>
              <form onSubmit={handleSubmit}>
                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      الاسم الأول
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      الاسم الأخير
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      البريد الإلكتروني
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      رقم الهاتف
                    </label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      كلمة المرور
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      السيرة الذاتية
                    </label>
                    <textarea
                      name="biography"
                      value={formData.biography}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={4}
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      سنوات الخبرة
                    </label>
                    <input
                      type="number"
                      name="yearsOfExperience"
                      value={formData.yearsOfExperience}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="0"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      كود المعلم
                    </label>
                    <input
                      type="number"
                      name="teacherCode"
                      value={formData.teacherCode}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      min="0"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      واتساب
                    </label>
                    <input
                      type="text"
                      name="whatsApp"
                      value={formData.whatsApp}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="رقم الواتساب"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      فيسبوك
                    </label>
                    <input
                      type="url"
                      name="facebook"
                      value={formData.facebook}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="رابط الفيسبوك"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      يوتيوب
                    </label>
                    <input
                      type="url"
                      name="youTube"
                      value={formData.youTube}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="رابط اليوتيوب"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      تيك توك
                    </label>
                    <input
                      type="url"
                      name="tikTok"
                      value={formData.tikTok}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 text-sm sm:text-base border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="رابط التيك توك"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-700 text-sm font-bold mb-1 sm:mb-2">
                      الصورة الشخصية
                    </label>
                    <div className="mt-1 flex flex-col items-center">
                      {!previewUrl ? (
                        <div className="w-full flex justify-center px-4 sm:px-6 pt-4 sm:pt-5 pb-5 sm:pb-6 border-2 border-gray-300 border-dashed rounded-md">
                          <div className="space-y-1 text-center">
                            <svg
                              className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400"
                              stroke="currentColor"
                              fill="none"
                              viewBox="0 0 48 48"
                              aria-hidden="true"
                            >
                              <path
                                d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                                strokeWidth={2}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            <div className="flex flex-col sm:flex-row text-xs sm:text-sm text-gray-600 items-center justify-center gap-1 sm:gap-0">
                              <label
                                htmlFor="image-upload"
                                className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                              >
                                <span>تحميل صورة</span>
                                <input
                                  id="image-upload"
                                  name="imageFile"
                                  type="file"
                                  accept="image/*"
                                  className="sr-only"
                                  onChange={handleFileChange}
                                />
                              </label>
                              <p className="hidden sm:inline pr-2">أو اسحب وأفلت</p>
                            </div>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF حتى 5MB</p>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2 sm:mt-4 w-full">
                          <div className="relative">
                            <img
                              src={previewUrl}
                              alt="Preview"
                              className="w-full max-h-48 sm:max-h-64 rounded-md border border-gray-200 object-contain"
                            />
                            <button
                              type="button"
                              className="absolute top-2 right-2 p-1.5 rounded-full bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                              onClick={() => {
                                setFormData((prev) => ({ ...prev, imageFile: null }));
                                setPreviewUrl(null);
                                setImageError(null);
                              }}
                              title="حذف الصورة"
                            >
                              <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                      
                      {imageError && (
                        <p className="mt-2 text-xs sm:text-sm text-red-600 text-center">{imageError}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 mt-4 sm:mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddForm(false);
                      setFormData({
                        firstName: '',
                        lastName: '',
                        email: '',
                        phoneNumber: '',
                        password: '',
                        biography: '',
                        yearsOfExperience: 0,
                        imagePath: '',
                        active: true,
                        subjectIds: [],
                        teacherCode: 0,
                        whatsApp: '',
                        facebook: '',
                        tikTok: '',
                        youTube: '',
                        imageFile: null,
                      });
                      setPreviewUrl(null);
                      setImageError(null);
                    }}
                    className="w-full sm:w-auto px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    إضافة المعلم
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {teacherToDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-3 sm:p-4">
            <div className="bg-white rounded-xl shadow-2xl p-4 sm:p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-4 sm:mb-5">
                <div className="mx-auto flex items-center justify-center h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-red-100 mb-3 sm:mb-4">
                  <TrashIcon className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">حذف المعلم</h3>
                <p className="text-sm sm:text-base text-gray-600">
                  هل أنت متأكد من حذف <span className="font-bold text-gray-900">{teacherToDelete.fullName}</span>؟
                  لا يمكن التراجع عن هذا الإجراء.
                </p>
              </div>
              <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setTeacherToDelete(null)}
                  disabled={isDeleting}
                >
                  إلغاء
                </button>
                <button
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 transition-all focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-70 flex items-center justify-center gap-2"
                  onClick={() => handleDelete(teacherToDelete.id)}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      جاري الحذف...
                    </>
                  ) : (
                    <>
                      <TrashIcon className="h-4 w-4" />
                      حذف
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Block Confirmation Modal */}
        {teacherToBlock && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm p-3 sm:p-4">
            <div className="bg-white rounded-xl shadow-2xl p-4 sm:p-6 w-full max-w-md border border-gray-200 transform transition-all">
              <div className="text-center mb-4 sm:mb-5">
                <div className="mx-auto flex items-center justify-center h-12 w-12 sm:h-16 sm:w-16 rounded-full bg-blue-100 mb-3 sm:mb-4">
                  <ExclamationTriangleIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" aria-hidden="true" />
                </div>
                <h3 className="text-lg sm:text-xl font-medium text-gray-900 mb-2">
                  {teacherToBlock.active ? 'حظر المعلم' : 'إلغاء حظر المعلم'}
                </h3>
                <p className="text-sm sm:text-base text-gray-600">
                  هل أنت متأكد من {teacherToBlock.active ? 'حظر' : 'إلغاء حظر'} المعلم <span className="font-bold text-gray-900">{teacherToBlock.fullName}</span>؟
                </p>
              </div>
              <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-end gap-3">
                <button
                  className="w-full sm:w-auto px-4 py-2 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-all focus:outline-none focus:ring-2 focus:ring-gray-300"
                  onClick={() => setTeacherToBlock(null)}
                >
                  إلغاء
                </button>
                <button
                  className={`w-full sm:w-auto px-4 py-2 rounded-lg ${
                    teacherToBlock.active
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-green-600 hover:bg-green-700'
                  } text-white font-medium transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    teacherToBlock.active
                      ? 'focus:ring-yellow-500'
                      : 'focus:ring-green-500'
                  }`}
                  onClick={() => handleToggleStatus(teacherToBlock)}
                >
                  {teacherToBlock.active ? 'حظر المعلم' : 'إلغاء حظر المعلم'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Teachers;
